import { useSyncExternalStore } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Button, Header, Menu, MenuItem, MenuSection, MenuTrigger, Popover, Separator } from 'react-aria-components';
import { useNavCount } from '../../hooks/useNavCounts';
import styles from './AppBar.module.css';

export interface NavItem {
  label: string;
  path: string;
  /** A page that starts something (Lodge): shown as an action, "+ Lodge". */
  action?: boolean;
}

interface AppBarProps {
  /** The surfaces, as pills: Today, then Verify and Release sitting for Oversight. */
  primary: NavItem[];
  /**
   * 13.2-11 (D-13.2-A-6): the most-used pages (Registers, Lodge), in the bar after More, quieter than the surfaces.
   * Below 1200 px they fold back into the More menu, so the phone bar is as before (ISS-197 kept).
   */
  pinned?: NavItem[];
  /** The existing pages, under the quieter "More" menu. */
  more: NavItem[];
  userDisplayName: string;
  /** e.g. "Oversight", "Care Manager". */
  roleLabel: string;
  onLogout: () => void;
}

function isActive(pathname: string, path: string): boolean {
  if (path === '/') return pathname === '/';
  return pathname === path || pathname.startsWith(`${path}/`);
}

function initialOf(name: string): string {
  return name.trim().charAt(0).toUpperCase() || '?';
}

/**
 * Below 1200 px the pinned pages fold back into More, so the phone and tablet bar is as before. Measured 25 Oct 2026
 * on the shell preview: Oversight's widest bar (three counts, a current "Closure trail" under More, the name shown)
 * clips by ~18 px at 1101 px and by ~120 px at 900 px once Registers and "+ Lodge" are added, so the plan's ~900 px
 * would clip. From 1200 px there is room to spare, including for a longer display name; 1280 and 1366 fit easily.
 */
const FOLD_QUERY = '(max-width: 1199.98px)';

function subscribeFold(onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => undefined;
  const mq = window.matchMedia(FOLD_QUERY);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

function isFolded(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(FOLD_QUERY).matches;
}

function PinnedLink({ item }: { item: NavItem }) {
  return (
    <NavLink to={item.path} className={item.action ? `${styles.pinned} ${styles.pinnedAction}` : styles.pinned}>
      {item.action && (
        <span className={styles.plus} aria-hidden="true">
          +
        </span>
      )}
      {item.label}
    </NavLink>
  );
}

function SurfaceLink({ item }: { item: NavItem }) {
  const count = useNavCount(item.path);
  return (
    <NavLink to={item.path} end={item.path === '/'} className={styles.surface}>
      {item.label}
      {count !== undefined && (
        <span className={styles.count} aria-label={`${count} waiting`}>
          {count}
        </span>
      )}
    </NavLink>
  );
}

/**
 * The app bar from the approved mock (design/mock-13, D-13-02-1): the transparent lotus and the
 * serif "Harmony" with a small "QMS", the surfaces as calm pills, the person on the right. The
 * product is "Harmony QMS" for everyone. The existing pages sit under a quieter "More" menu
 * (React Aria: keyboard, focus and dismissal handled). 13.2-11: Registers and "+ Lodge" sit after More,
 * quieter than the surfaces, and fold back into More below 1200 px.
 */
export default function AppBar({ primary, pinned = [], more, userDisplayName, roleLabel, onLogout }: AppBarProps) {
  const { pathname } = useLocation();
  const folded = useSyncExternalStore(subscribeFold, isFolded, () => false);
  const inBar = folded ? [] : pinned;
  const menu = folded ? [...pinned, ...more] : more;
  const moreActive = menu.find((m) => isActive(pathname, m.path));

  return (
    <header className={styles.appbar}>
      <div className={styles.inner}>
        <NavLink to="/" end className={styles.brand} aria-label="Harmony QMS, Today">
          <img src="/logos/harmony-flower.png" alt="" className={styles.logo} />
          <b className={styles.wordmark}>Harmony</b>
          <small className={styles.product}>QMS</small>
        </NavLink>

        <nav className={styles.surfaces} aria-label="Surfaces">
          {primary.map((item) => (
            <SurfaceLink key={item.path} item={item} />
          ))}

          {menu.length > 0 && (
            <MenuTrigger>
              <Button className={styles.more} data-current={moreActive ? 'true' : undefined}>
                {moreActive ? (
                  <>
                    {/* ISS-197: at phone width the long page name ("Closure trail") was clipped; it reads "More" there. */}
                    <span className={styles.moreLabel}>{moreActive.label}</span>
                    <span className={styles.moreShort}>More</span>
                  </>
                ) : (
                  'More'
                )}
                <svg className={styles.chev} viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </Button>
              <Popover className={styles.popover} placement="bottom start" offset={8}>
                <Menu className={styles.menu} aria-label="More pages">
                  {menu.map((item) => (
                    <MenuItem
                      key={item.path}
                      id={item.path}
                      href={item.path}
                      className={styles.menuItem}
                      data-current={isActive(pathname, item.path) ? 'true' : undefined}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Menu>
              </Popover>
            </MenuTrigger>
          )}

          {inBar.length > 0 && (
            <>
              <span className={styles.divider} aria-hidden="true" />
              {inBar.map((item) => (
                <PinnedLink key={item.path} item={item} />
              ))}
            </>
          )}
        </nav>

        <MenuTrigger>
          <Button className={styles.me} aria-label={`${userDisplayName}, ${roleLabel}. Account menu`}>
            <span className={styles.who}>
              <b>{userDisplayName}</b>
              <span>{roleLabel}</span>
            </span>
            <span className={styles.avatar} aria-hidden="true">
              {initialOf(userDisplayName)}
            </span>
          </Button>
          <Popover className={styles.popover} placement="bottom end" offset={8}>
            <Menu className={styles.menu} aria-label="Account" onAction={(key) => key === 'signout' && onLogout()}>
              <MenuSection>
                <Header className={styles.menuHeader}>
                  {userDisplayName}
                  <span>{roleLabel}</span>
                </Header>
              </MenuSection>
              <Separator className={styles.sep} />
              <MenuItem id="signout" className={styles.menuItem}>
                Sign out
              </MenuItem>
            </Menu>
          </Popover>
        </MenuTrigger>
      </div>
    </header>
  );
}
